// Simple Slider Web Component

if (!customElements.get('simple-slider')) {
  class SimpleSlider extends HTMLElement {
    constructor() {
      super();

      // Slider elements
      this.sliderList = this.querySelector('[data-list]');
      this.slideLeft = this.querySelector('[data-slide-left]');
      this.slideRight = this.querySelector('[data-slide-right]');
      this.dots = this.querySelectorAll('[data-dot]');
      this.thumbnails = this.querySelector('[data-thumbnails]');
      this.thumbnailLeft = this.querySelector('[data-thumbnail-left]');
      this.thumbnailRight = this.querySelector('[data-thumbnail-right]');
      this.number = this.querySelector('[data-number]');
      this.info = this.querySelector('[data-info]');

      // Initial state
      this.slide = this.sliderList?.children[0] || null;
      this.thumbnail = this.thumbnails?.children[0] || null
      this.currentIndex = 0;
      this.isPointerDown = false;
      this.startX = 0;
      this.scrollLeftStart = 0;
      this.dragged = false; // Track whether dragging occurred
      this.hasScrolled = false; // Ensure only one scroll per click
      this.size = this.sliderList.children.length;
      this.isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');

      if (this.isSafari) {
        this.sensitivity = 1.5; // Adjust sensitivity for Safari
      } else {
        this.sensitivity = 1;
      }
      this.setupEventListeners();
      this.setupObservers();
      this.visibilityDots();
      this.visibilityNavigation();
      this.visibilityThumbnails();
    }

    setupEventListeners() {
      this.slideLeft?.addEventListener('click', () => this.scrollSliderLeft());
      this.slideRight?.addEventListener('click', () => this.scrollSliderRight());

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', (e) => {
          this.scrollToSlide(index);
        });
      });

      if (this.thumbnails) {
        [...this.thumbnails.children].forEach((thumb, index) => {
          thumb.addEventListener("click", () => {
            this.scrollToSlide(index);
          });
        });
      }

      this.thumbnailLeft?.addEventListener('click', () => this.scrollThumbnailLeft());
      this.thumbnailRight?.addEventListener('click', () => this.scrollThumbnailRight());

      if (window.innerWidth >= 750) {
        // Pointer (Mouse & Touch) drag support
        this.sliderList.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.sliderList.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.sliderList.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.sliderList.addEventListener('pointerleave', this.onPointerUp.bind(this));

        // Prevent link navigation on drag
        this.sliderList.querySelectorAll('a').forEach((link) => {
          link.addEventListener('click', (event) => {
            if (this.dragged) {
              event.preventDefault();
            }
          });
        });
      }

      this.info?.addEventListener('click', () => {
        this.scrollToImportantSlide();
      });
    }

    onPointerDown(event) {
      this.isPointerDown = true;
      this.startX = event.pageX;
      this.scrollLeftStart = this.sliderList.scrollLeft;
      this.dragged = false; // Reset dragged state on new drag
      this.hasScrolled = false; // Allow scrolling again
      this.sliderList.style.cursor = 'grabbing'; // Change cursor to indicate dragging
      event.preventDefault(); // Prevent unintended selection
    }

    onPointerMove(event) {
      if (!this.isPointerDown || this.hasScrolled) return;
      const moveX = (event.pageX - this.startX) * this.sensitivity;

      if (Math.abs(moveX) > 2) { // Reduced sensitivity for quicker response
        this.dragged = true;
      }

      if (Math.abs(moveX) > 20) { // Set threshold for one-time scroll
        if (moveX > 0) {
          this.scrollSliderLeft();
        } else {
          this.scrollSliderRight();
        }
        this.hasScrolled = true; // Prevent further scrolling until next click
      }
    }

    onPointerUp() {
      this.isPointerDown = false;
      this.sliderList.style.cursor = 'grab'; // Reset cursor
    }

    scrollSliderLeft() {
      this.sliderList.scrollLeft -= this.slide.clientWidth;
    }

    scrollSliderRight() {
      this.sliderList.scrollLeft += this.slide.clientWidth;
    }

    scrollToSlide(index) {
      if (index < 0 || index >= this.sliderList.children.length) return;
      this.sliderList.scrollTo({ left: this.slide.clientWidth * index, behavior: 'smooth' });
    }

    scrollToImportantSlide() {
      const slides = [...this.sliderList.children];
      const importantSlideIndex = slides.findIndex(slide => slide.hasAttribute('data-important'));

      if (importantSlideIndex !== -1) {
        this.scrollToSlide(importantSlideIndex);
      }
    }

    setupObservers() {
      // Intersection Observer for slides
      const threshold = 0.8;
      const rootMargin = `0px 0px 0px 0px`;
      const observerOptions = {
        threshold,
        rootMargin,
      };

      const sliderScrollObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.currentIndex = parseInt(entry.target.dataset.index, 10);
            this.visibilityNavigation();
            this.visibilityDots();
            this.visibilityThumbnails();
            this.setCurrentNumberOfSlide();
            this.scrollThumbnailIntoView();
            console.log("Current index:", this.currentIndex, 'Slides per view:', this.getSlidesPerView());
          }
        });
      }, observerOptions);

      [...this.sliderList.children].forEach((slide, index) => {
        slide.dataset.index = index;
        sliderScrollObserver.observe(slide);
      });
    }


    getSlidesPerView() {
      const sliderContainer = this.sliderList;
      const slides = [...this.sliderList.children];

      if (!sliderContainer || slides.length === 0) return 1;

      const containerWidth = sliderContainer.clientWidth;
      const slideWidth = slides[0].clientWidth;
      const firstSlideOffset = slides[0].offsetLeft; // Distance from the left edge of the container
      const gap = 24; // Fixed gap between slides

      // Subtract the gap for all but the last slide
      const adjustedWidth = containerWidth - firstSlideOffset;

      // Calculate the number of slides per view, including the gap
      const slidesPerView = Math.max(1, Math.floor((adjustedWidth + gap) / (slideWidth + gap)));

      return slidesPerView;
    }

    // Method to scroll thumbnails if the current index is out of view
    scrollThumbnailIntoView() {
      if (this.thumbnails) {
        if (this.thumbnails.hasAttribute('data-vertical')) {
          this.scrollThumbnailVertically();
        } else if (this.thumbnails.hasAttribute('data-horizontal')) {
          this.scrollThumbnailHorizontally();
        }
      }
    }

    scrollThumbnailLeft() {
      this.thumbnails.scrollLeft -= this.thumbnail.clientWidth;

    }

    scrollThumbnailRight() {
      this.thumbnails.scrollLeft += this.thumbnail.clientWidth;

    }

    scrollThumbnailVertically() {
      const container = this.thumbnails;
      const thumbnails = [...container.children];
      const currentThumb = thumbnails[this.currentIndex];

      if (!currentThumb) return; // Safety check

      const containerTop = container.scrollTop;
      const containerBottom = containerTop;
      const thumbTop = currentThumb.offsetTop - container.offsetTop;
      const thumbBottom = thumbTop + currentThumb.clientHeight + currentThumb.clientHeight;

      // Scroll up if the thumbnail is above the visible area
      if (thumbTop < containerTop) {
        container.scrollTo({
          top: thumbTop,
          behavior: "smooth",
        });
      }
      // Scroll down if the thumbnail is below the visible area
      else if (thumbBottom > containerBottom) {
        container.scrollTo({
          top: thumbBottom - container.clientHeight,
          behavior: "smooth",
        });
      }
    }

    scrollThumbnailHorizontally() {
      const container = this.thumbnails;
      const thumbnails = [...container.children];
      const currentThumb = thumbnails[this.currentIndex];

      if (!currentThumb) return; // Safety check

      const containerLeft = container.scrollLeft;
      const containerRight = containerLeft;
      const thumbLeft = currentThumb.offsetLeft - container.offsetLeft;
      const thumbRight = thumbLeft + currentThumb.clientWidth + currentThumb.clientWidth;

      if (thumbLeft < containerLeft) {
        container.scrollTo({ left: thumbLeft, behavior: "smooth" });
      } else if (thumbRight > containerRight) {
        container.scrollTo({ left: thumbRight - container.clientWidth, behavior: "smooth" });
      }
    }

    visibilityNavigation() {
      if (this.currentIndex === 0) {
        this.slideLeft?.setAttribute('data-active', 'false');
      } else {
        this.slideLeft?.setAttribute('data-active', 'true');
      }

      if (this.currentIndex >= (this.size - this.getSlidesPerView())) {
        this.slideRight?.setAttribute('data-active', 'false');
      } else {
        this.slideRight?.setAttribute('data-active', 'true');
      }
    }

    visibilityDots() {
      this.dots.forEach((dot) => dot?.removeAttribute('data-active'));
      this.dots[this.currentIndex]?.setAttribute('data-active', '');
    }

    visibilityThumbnails() {
      if (this.thumbnails) {
        [...this.thumbnails.children].forEach((thumb) => thumb?.removeAttribute('data-active'));
        [...this.thumbnails.children][this.currentIndex]?.setAttribute('data-active', '');
      }
    }

    setCurrentNumberOfSlide() {
      if (this.number) {
        this.number.textContent = this.currentIndex + 1;
      }
    }

  }

  customElements.define('simple-slider', SimpleSlider);
}
